<template>
  <div class="absolute inset-0">
    <!-- Nach Zustimmung: datenschutzfreundliches Embed (YouTube über youtube-nocookie.com, Vimeo mit dnt=1) -->
    <iframe
      v-if="loaded"
      :src="embedSrc"
      :title="title"
      class="absolute inset-0 h-full w-full"
      frameborder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowfullscreen
      referrerpolicy="strict-origin-when-cross-origin"
    ></iframe>

    <!-- Click-to-load-Fassade: Poster + Play-Button + Datenschutzhinweis -->
    <template v-else>
      <img
        v-if="posterSrc"
        :src="posterSrc"
        :alt="title"
        class="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
      />
      <!-- /60 statt /40: der Hinweistext ist weiß und liegt über einem
           beliebigen Vorschaubild. Erst ab 60 % Abdunklung erreicht er auch
           über einem rein weißen Poster die 4.5:1 aus WCAG AA. -->
      <div class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/60 p-4 text-center">
        <button
          type="button"
          class="flex cursor-pointer flex-col items-center gap-3"
          :aria-label="'Inhalt von ' + providerLabel + ' laden: ' + title"
          @click="load"
        >
          <span class="flex h-16 w-16 items-center justify-center rounded-full bg-accent-1 text-white shadow-lg transition-transform hover:scale-110">
            <svg class="ml-1 h-7 w-7" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
          <span class="max-w-md text-sm font-medium leading-snug text-white">{{ consentNote }}</span>
        </button>

        <!-- Art. 7 Abs. 3 DSGVO: das Merken ist freiwillig und endet spätestens
             mit der Sitzung. Deshalb Opt-in per Häkchen, NICHT vorausgewählt. -->
        <label class="flex cursor-pointer items-center gap-2 text-xs text-white">
          <input v-model="remember" type="checkbox" class="h-4 w-4 accent-accent-1" />
          <span>Entscheidung für diese Sitzung merken</span>
        </label>

        <a
          v-if="privacyUrl"
          :href="privacyUrl"
          class="text-xs text-white underline"
        >Datenschutzerklärung</a>
      </div>
    </template>
  </div>
</template>

<script>
// Click-to-load-Fassade für externe Einbettungen (YouTube, Vimeo, Google Maps).
// Wird in bootstrap.js auf [data-vue-video-consent] gemountet; Props kommen aus
// den data-Attributen des Mount-Elements:
//   data-video-url / data-iframe-src – src-URL des Original-Embeds
//   data-provider    – 'youtube' | 'vimeo' | 'maps' (sonst Erkennung aus URL)
//   data-poster-src  – optional: Vorschaubild-URL
//   data-title       – optional: iframe-Titel / Alt-Text
//   data-privacy-url – optional: Link zur Datenschutzerklärung
//
// Die Einwilligung erteilt der KLICK auf diese Fassade — konkret, informiert
// und pro Anbieter. Bewusst KEINE Kopplung an das Matomo-Banner: das ist ein
// anderer Zweck und ein anderer Empfänger; ein globales "Zustimmen" dürfte
// hier nichts freischalten (Art. 4 Nr. 11 DSGVO).
import { EVENT_EMBED, embedRemembered, rememberEmbed } from '../consent.js'

// Empfänger im Klartext. Wer einwilligt, muss wissen WEM gegenüber — und dass
// dabei Daten in ein Drittland fließen können (Art. 13 Abs. 1 lit. e/f DSGVO).
const PROVIDERS = {
  youtube: {
    label: 'YouTube',
    note:
      'Video von YouTube laden? Dabei werden Ihre IP-Adresse und Gerätedaten an Google Ireland Ltd. übertragen, mögliche Weitergabe in die USA.',
  },
  vimeo: {
    label: 'Vimeo',
    note:
      'Video von Vimeo laden? Dabei werden Ihre IP-Adresse und Gerätedaten an Vimeo Inc. in die USA übertragen.',
  },
  maps: {
    label: 'Google Maps',
    note:
      'Karte von Google Maps laden? Dabei werden Ihre IP-Adresse und Gerätedaten an Google Ireland Ltd. übertragen, mögliche Weitergabe in die USA.',
  },
}

export default {
  name: 'VideoConsent',
  props: {
    videoUrl: {
      type: String,
      default: '',
    },
    provider: {
      type: String,
      default: '',
    },
    posterSrc: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: 'Video',
    },
    privacyUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loaded: false,
      remember: false,
    }
  },
  computed: {
    detectedProvider() {
      if (PROVIDERS[this.provider]) {
        return this.provider
      }
      if (/youtube(?:-nocookie)?\.com|youtu\.be/.test(this.videoUrl)) {
        return 'youtube'
      }
      if (/vimeo\.com/.test(this.videoUrl)) {
        return 'vimeo'
      }
      if (/google\.[a-z.]+\/maps/.test(this.videoUrl)) {
        return 'maps'
      }
      return ''
    },
    providerLabel() {
      return PROVIDERS[this.detectedProvider]?.label || 'einem externen Anbieter'
    },
    consentNote() {
      return (
        PROVIDERS[this.detectedProvider]?.note ||
        'Externen Inhalt laden? Dabei werden Ihre IP-Adresse und Gerätedaten an den Anbieter übertragen.'
      )
    },
    youtubeId() {
      const match = this.videoUrl.match(
        /(?:youtube(?:-nocookie)?\.com\/(?:embed\/|shorts\/|live\/|watch\?(?:.*&)?v=)|youtu\.be\/)([\w-]{6,})/
      )
      return match ? match[1] : ''
    },
    vimeoId() {
      const match = this.videoUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/)
      return match ? match[1] : ''
    },
    embedSrc() {
      if (this.detectedProvider === 'youtube' && this.youtubeId) {
        return 'https://www.youtube-nocookie.com/embed/' + this.youtubeId + '?autoplay=1&rel=0'
      }
      if (this.detectedProvider === 'vimeo' && this.vimeoId) {
        return 'https://player.vimeo.com/video/' + this.vimeoId + '?autoplay=1&dnt=1'
      }
      // Karten dürfen nicht automatisch abspielen — autoplay nur für Video.
      if (this.detectedProvider === 'maps') {
        return this.videoUrl
      }
      // Fallback: Original-URL, Autoplay anhängen
      return this.videoUrl + (this.videoUrl.includes('?') ? '&' : '?') + 'autoplay=1'
    },
  },
  mounted() {
    // Wurde für diesen Anbieter in dieser Sitzung schon zugestimmt, direkt
    // laden. Andere Anbieter bleiben davon unberührt.
    if (embedRemembered(this.detectedProvider)) {
      this.loaded = true
      return
    }
    // Mehrere Einbettungen desselben Anbieters auf einer Seite: hakt der Nutzer
    // bei einer "merken" an, laden die übrigen sofort mit.
    this._onConsent = (event) => {
      if (event.detail?.provider === this.detectedProvider) {
        this.loaded = true
      }
    }
    window.addEventListener(EVENT_EMBED, this._onConsent)
  },
  beforeUnmount() {
    if (this._onConsent) {
      window.removeEventListener(EVENT_EMBED, this._onConsent)
    }
  },
  methods: {
    load() {
      if (!this.videoUrl) return
      this.loaded = true
      if (this.remember) {
        rememberEmbed(this.detectedProvider)
      }
    },
  },
}
</script>
